import React, { useState } from 'react';
import { X, Video, MapPin, Lock, CreditCard, CheckCircle, Loader, ChevronRight, RefreshCw } from 'lucide-react';
import { TIME_SLOTS_30MIN } from '../data/amenities';
import { useAuth } from '../context/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const API = process.env.REACT_APP_BACKEND_URL;
const INSPECTION_FEE = 15;
const PLATFORMS = ['Zoom', 'Microsoft Teams', 'Google Meet'];

const PLATFORM_LINKS = {
  'Zoom': 'https://zoom.us/j/rentalx',
  'Microsoft Teams': 'https://teams.microsoft.com/l/rentalx',
  'Google Meet': 'https://meet.google.com/rentalx',
};

// ── Step indicators ────────────────────────────────────────────────────────
const Steps = ({ step, isVirtual }) => {
  const steps = isVirtual
    ? ['Type', 'Details', 'Payment', 'Confirmed']
    : ['Type', 'Details', 'Confirmed'];
  return (
    <div className="flex items-center justify-center gap-1 mb-6">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-1 ${i + 1 <= step ? 'text-[#094f6f]' : 'text-gray-300'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
              i + 1 < step ? 'bg-[#094f6f] border-[#094f6f] text-white' :
              i + 1 === step ? 'border-[#094f6f] text-[#094f6f]' : 'border-gray-200 text-gray-300'
            }`}>{i + 1 < step ? '✓' : i + 1}</div>
            <span className="text-xs hidden sm:block">{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-8 h-0.5 ${i + 1 < step ? 'bg-[#094f6f]' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── Payment form ────────────────────────────────────────────────────────────
const PaymentForm = ({ property, date, slot, platform, onPay, paying }) => {
  const [method, setMethod] = useState('card');
  const [card, setCard] = useState({ number: '', expiry: '', cvv: '', name: '' });

  return (
    <div className="space-y-4">
      <div className="bg-sky-50 rounded-xl p-4 border border-sky-100 text-sm">
        <p className="font-semibold text-gray-800 mb-1">{property.name}</p>
        <p className="text-gray-500">{date?.toLocaleDateString('en-AU')} · {slot} · {platform}</p>
        <div className="flex justify-between mt-2 pt-2 border-t border-sky-200">
          <span className="text-gray-600">Virtual Inspection Fee</span>
          <span className="font-bold text-[#094f6f]">A${INSPECTION_FEE}</span>
        </div>
        <p className="text-[10px] text-green-600 mt-1">✓ Fully refunded if you proceed to book</p>
      </div>

      <div className="flex gap-2">
        {[
          { id: 'card', label: 'Card', icons: ['VISA', 'MC'] },
          { id: 'paypal', label: 'PayPal', icons: ['PP'] },
        ].map(m => (
          <button key={m.id} onClick={() => setMethod(m.id)}
            className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              method === m.id ? 'border-[#094f6f] bg-sky-50 text-[#094f6f]' : 'border-gray-200 text-gray-600'
            }`}>
            {m.label}
          </button>
        ))}
      </div>

      {method === 'card' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Card Number</label>
            <input value={card.number} onChange={e => setCard({...card, number: e.target.value.replace(/\D/g,'').slice(0,16).replace(/(.{4})/g,'$1 ').trim()})}
              placeholder="1234 5678 9012 3456" maxLength={19}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 focus:border-[#094f6f] tracking-widest" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">Expiry</label>
              <input value={card.expiry} onChange={e => setCard({...card, expiry: e.target.value})}
                placeholder="MM / YY" maxLength={7}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 block mb-1">CVV</label>
              <input value={card.cvv} onChange={e => setCard({...card, cvv: e.target.value.replace(/\D/g,'').slice(0,4)})}
                placeholder="•••" maxLength={4} type="password"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Cardholder Name</label>
            <input value={card.name} onChange={e => setCard({...card, name: e.target.value})}
              placeholder="Name on card"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
          </div>
        </div>
      )}

      {method === 'paypal' && (
        <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
          <p className="text-sm text-blue-700 font-medium mb-1">You'll be redirected to PayPal</p>
          <p className="text-xs text-blue-500">After completing payment you'll return to confirm your booking.</p>
        </div>
      )}

      <button onClick={() => onPay(method)} disabled={paying}
        className="w-full py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90 transition-opacity"
        style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
        {paying ? <Loader className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
        {paying ? 'Processing...' : `Pay A$${INSPECTION_FEE} Securely`}
      </button>

      <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
        <Lock className="w-3 h-3" />
        <span>256-bit SSL encryption · PCI DSS compliant</span>
      </div>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────
const InspectionBooking = ({ property, onClose }) => {
  const { user, token } = useAuth();
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null); // 'virtual' | 'inperson'
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState('');
  const [platform, setPlatform] = useState('Zoom');
  const [paying, setPaying] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');

  const isVirtual = type === 'virtual';
  const maxStep = isVirtual ? 4 : 3;

  const handlePay = async (method) => {
    setPaying(true);
    try {
      // Create Stripe checkout session on backend
      const res = await fetch(`${API}/api/payments/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          package_id: 'virtual_inspection',
          origin_url: window.location.origin,
          property_id: property.id || '',
          property_name: property.name,
          property_city: property.city || property.location || '',
          room_type: property.roomTypes?.[0] || property.room_type || '',
          inspection_date: date?.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
          inspection_slot: slot,
          platform,
          student_email: user?.email || '',
          student_name: user?.full_name || 'Student',
          landlord_email: property.landlord_email || '',
          landlord_name: 'Property Owner',
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        // Redirect to Stripe hosted checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || 'Failed to create checkout');
      }
    } catch (e) {
      setPaying(false);
      alert(`Payment error: ${e.message}`);
    }
  };

  const handleInPersonRequest = async () => {
    try {
      await fetch(`${API}/api/email/inspection-booked`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_email: user?.email || '',
          student_name: user?.full_name || 'Student',
          landlord_email: property.landlord_email || 'landlord@rentalx.io',
          landlord_name: 'Property Owner',
          property_name: property.name,
          date: date?.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
          slot,
          method: 'In-Person',
        }),
      });
    } catch { /* silent */ }
    setStep(3);
  };

  const confStep = isVirtual ? 4 : 3;

  return (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={step !== confStep ? onClose : undefined} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
          <div className="text-white">
            <p className="font-bold">Book Inspection</p>
            <p className="text-xs text-white/70 truncate max-w-[240px]">{property.name}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <Steps step={step} isVirtual={isVirtual} />

          {/* STEP 1: Type selection */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 text-center mb-4">How would you like to inspect this property?</p>
              {[
                { id: 'virtual', icon: Video, title: 'Virtual Inspection', desc: `Live video tour · A$${INSPECTION_FEE} (refundable if you book)`, color: 'border-[#094f6f]' },
                { id: 'inperson', icon: MapPin, title: 'In-Person Inspection', desc: 'Visit the property in person · Free', color: 'border-[#2cbdc4]' },
              ].map(opt => (
                <button key={opt.id} onClick={() => { setType(opt.id); setStep(2); }}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 hover:shadow-md transition-all text-left ${
                    type === opt.id ? opt.color + ' bg-sky-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
                    <opt.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{opt.title}</p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                </button>
              ))}
            </div>
          )}

          {/* STEP 2: Details */}
          {step === 2 && (
            <div className="space-y-4">
              {isVirtual && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700">
                  <span>💳</span>
                  <span>A${INSPECTION_FEE} inspection fee · <strong>fully refunded</strong> if you book the property</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
                <DatePicker selected={date} onChange={setDate} minDate={new Date()}
                  placeholderText="Select inspection date" dateFormat="dd/MM/yyyy"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30"
                  wrapperClassName="w-full" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Slot</label>
                <div className="grid grid-cols-3 gap-1.5 max-h-36 overflow-y-auto pr-1">
                  {TIME_SLOTS_30MIN.map(t => (
                    <button key={t} type="button" onClick={() => setSlot(t)}
                      className={`py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        slot === t ? 'text-white border-transparent' : 'border-gray-200 text-gray-600 hover:border-[#094f6f]'
                      }`}
                      style={slot === t ? { background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' } : {}}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {isVirtual && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Video Platform</label>
                  <div className="flex gap-2">
                    {PLATFORMS.map(p => (
                      <button key={p} onClick={() => setPlatform(p)}
                        className={`flex-1 py-2 rounded-xl text-xs font-medium border transition-all ${
                          platform === p ? 'text-white border-transparent' : 'border-gray-200 text-gray-600'
                        }`}
                        style={platform === p ? { background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' } : {}}>
                        {p === 'Zoom' ? 'Zoom' : p === 'Microsoft Teams' ? 'Teams' : 'Meet'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!isVirtual && (
                <div className="bg-sky-50 border border-sky-100 rounded-xl p-4 text-sm text-gray-600">
                  <p className="font-semibold text-gray-800 mb-1">📍 In-Person Inspection</p>
                  <p>The landlord/agent will contact you to confirm the inspection address and exact time.</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button onClick={() => setStep(1)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50">
                  Back
                </button>
                <button
                  onClick={() => {
                    if (!date || !slot) return;
                    if (isVirtual) setStep(3);
                    else handleInPersonRequest();
                  }}
                  disabled={!date || !slot}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
                  {isVirtual ? 'Securely Book →' : 'Request Inspection →'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 (virtual only): Payment */}
          {step === 3 && isVirtual && (
            <div>
              <button onClick={() => setStep(2)} className="text-xs text-[#094f6f] mb-4 flex items-center gap-1 hover:opacity-70">
                ← Back
              </button>
              <PaymentForm property={property} date={date} slot={slot} platform={platform} onPay={handlePay} paying={paying} />
            </div>
          )}

          {/* CONFIRMATION step */}
          {step === confStep && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-9 h-9 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isVirtual ? 'Virtual Inspection Confirmed!' : 'Inspection Request Sent!'}
              </h3>
              {isVirtual ? (
                <>
                  <div className="bg-sky-50 rounded-xl p-4 text-left text-sm space-y-1.5 mb-4">
                    <p className="text-gray-500">📍 {property.name}</p>
                    <p className="text-gray-500">📅 {date?.toLocaleDateString('en-AU', {weekday:'long',day:'numeric',month:'long'})}</p>
                    <p className="text-gray-500">🕐 {slot}</p>
                    <p className="text-gray-500">📹 {platform}</p>
                    {meetingLink && <p className="text-[#094f6f] text-xs break-all font-mono bg-sky-100 p-2 rounded">🔗 {meetingLink}</p>}
                  </div>
                  <p className="text-xs text-gray-400 mb-4">Confirmation and meeting link sent to your email ✓</p>
                </>
              ) : (
                <p className="text-sm text-gray-500 mb-4">
                  The landlord/agent will contact you shortly to confirm the inspection address and time.
                  A confirmation has been sent to your email.
                </p>
              )}
              <button onClick={onClose}
                className="w-full py-3 rounded-xl font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #2cbdc4, #094f6f)' }}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionBooking;
